import type { ReactNode } from "react"

import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

import { SearchableSelect } from "./searchable-select"
import { SameOrDifferentToggle } from "./same-or-different-toggle"
import type { CompanyDetails, Industry, ShareChoice } from "./types"
import { INDUSTRY_OPTIONS } from "./types"

type ShareField = "domain" | "slug" | "logo"

type CompanyDetailsFieldsProps = {
  value: CompanyDetails
  onChange: (next: CompanyDetails) => void
  errors?: Record<string, string>
  shareModes?: {
    domain: ShareChoice
    slug: ShareChoice
    logo: ShareChoice
  }
  onShareModeChange?: (field: ShareField, mode: ShareChoice) => void
  primary?: {
    domain: string
    slug: string
    logoFile: File | null
  }
  fieldIdPrefix?: string
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <div className="text-sm font-medium">{children}</div>
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <div className="text-xs text-red-600">{message}</div>
}

export function CompanyDetailsFields({
  value,
  onChange,
  errors = {},
  shareModes,
  onShareModeChange,
  primary,
  fieldIdPrefix = "",
}: CompanyDetailsFieldsProps) {
  const showShareToggles = Boolean(shareModes && onShareModeChange && primary)
  const domainMode = shareModes?.domain ?? "different"
  const slugMode = shareModes?.slug ?? "different"
  const logoMode = shareModes?.logo ?? "different"
  const domainValue = showShareToggles && domainMode === "same" ? primary?.domain ?? "" : value.domain
  const slugValue = showShareToggles && slugMode === "same" ? primary?.slug ?? "" : value.slug
  const logoFile =
    showShareToggles && logoMode === "same" ? primary?.logoFile ?? null : value.logoFile

  function patch(next: Partial<CompanyDetails>) {
    onChange({ ...value, ...next })
  }

  return (
    <>
      <div className="space-y-2">
        <FieldLabel>Company Name</FieldLabel>
        <Input
          id={`${fieldIdPrefix}companyName`}
          value={value.companyName}
          onChange={(event) => patch({ companyName: event.target.value })}
          placeholder="e.g. Acme Pvt Ltd"
        />
        <FieldError message={errors.companyName} />
      </div>

      <div className="space-y-2">
        <FieldLabel>Plan</FieldLabel>
        <select
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={value.plan}
          onChange={(event) => patch({ plan: event.target.value as CompanyDetails["plan"] })}
        >
          <option value="Starter">Starter</option>
          <option value="Growth">Growth</option>
          <option value="Enterprise">Enterprise</option>
        </select>
      </div>

      <div className="space-y-2">
        <FieldLabel>Contact Person Name</FieldLabel>
        <Input
          value={value.contactPersonName}
          onChange={(event) => patch({ contactPersonName: event.target.value })}
          placeholder="e.g. John Doe"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>Contact Person Email</FieldLabel>
        <Input
          id={`${fieldIdPrefix}contactPersonEmail`}
          value={value.contactPersonEmail}
          onChange={(event) => patch({ contactPersonEmail: event.target.value })}
          placeholder="name@company.com"
          type="email"
        />
        <FieldError message={errors.contactPersonEmail} />
      </div>

      <div className="space-y-2">
        <FieldLabel>Contact Person No</FieldLabel>
        <Input
          value={value.contactPersonNo}
          onChange={(event) => patch({ contactPersonNo: event.target.value })}
          placeholder="Phone number"
          inputMode="numeric"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>Industry</FieldLabel>
        <SearchableSelect
          value={value.industry}
          options={INDUSTRY_OPTIONS.map((industry) => ({ value: industry, label: industry }))}
          onChange={(next) => patch({ industry: next as Industry })}
          placeholder="Select industry"
          searchPlaceholder="Search industry..."
        />
      </div>

      <div className="space-y-2 md:col-span-2">
        <FieldLabel>Address</FieldLabel>
        <Textarea
          value={value.address}
          onChange={(event) => {
            const address = event.target.value
            patch({
              address,
              billingAddress: value.billingSameAsAddress ? address : value.billingAddress,
            })
          }}
          placeholder="Full address"
          className="min-h-[96px]"
        />
      </div>

      <div className="space-y-2 md:col-span-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <FieldLabel>Billing Address</FieldLabel>
          <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={value.billingSameAsAddress}
              onChange={(event) => {
                const checked = event.target.checked
                patch({
                  billingSameAsAddress: checked,
                  billingAddress: checked ? value.address : value.billingAddress,
                })
              }}
            />
            Same as above
          </label>
        </div>
        <Textarea
          value={value.billingSameAsAddress ? value.address : value.billingAddress}
          onChange={(event) => patch({ billingAddress: event.target.value })}
          placeholder="Billing address"
          className="min-h-[96px]"
          disabled={value.billingSameAsAddress}
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>GST No.</FieldLabel>
        <Input
          id={`${fieldIdPrefix}gstNo`}
          value={value.gstNo}
          onChange={(event) => patch({ gstNo: event.target.value })}
          placeholder="15 alphanumeric"
        />
        <FieldError message={errors.gstNo} />
      </div>

      <div className="space-y-2">
        <FieldLabel>PAN</FieldLabel>
        <Input
          id={`${fieldIdPrefix}pan`}
          value={value.pan}
          onChange={(event) => patch({ pan: event.target.value })}
          placeholder="10 alphanumeric"
        />
        <FieldError message={errors.pan} />
      </div>

      <div className="space-y-2">
        <FieldLabel>TAN</FieldLabel>
        <Input
          id={`${fieldIdPrefix}tan`}
          value={value.tan}
          onChange={(event) => patch({ tan: event.target.value })}
          placeholder="10 alphanumeric"
        />
        <FieldError message={errors.tan} />
      </div>

      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <FieldLabel>Logo</FieldLabel>
          {showShareToggles && (
            <SameOrDifferentToggle
              name={`${fieldIdPrefix}logoMode`}
              value={logoMode}
              onChange={(mode) => onShareModeChange?.("logo", mode)}
            />
          )}
        </div>
        {showShareToggles && logoMode === "same" ? (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            {logoFile
              ? `Using primary logo: ${logoFile.name}`
              : "Using the same logo as the primary company."}
          </div>
        ) : (
          <>
            <Input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null
                patch({ logoFile: file })
              }}
            />
            {value.logoFile && (
              <div className="text-xs text-muted-foreground">Selected: {value.logoFile.name}</div>
            )}
          </>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <FieldLabel>Domain</FieldLabel>
          {showShareToggles && (
            <SameOrDifferentToggle
              name={`${fieldIdPrefix}domainMode`}
              value={domainMode}
              onChange={(mode) => onShareModeChange?.("domain", mode)}
            />
          )}
        </div>
        <Input
          id={`${fieldIdPrefix}domain`}
          value={domainValue}
          onChange={(event) => patch({ domain: event.target.value })}
          placeholder="e.g. tenant1.unnati.com"
          disabled={showShareToggles && domainMode === "same"}
        />
        {showShareToggles && domainMode === "same" && (
          <div className="text-xs text-muted-foreground">Using the primary company domain.</div>
        )}
        <FieldError message={errors.domain} />
      </div>

      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <FieldLabel>Slug</FieldLabel>
          {showShareToggles && (
            <SameOrDifferentToggle
              name={`${fieldIdPrefix}slugMode`}
              value={slugMode}
              onChange={(mode) => onShareModeChange?.("slug", mode)}
            />
          )}
        </div>
        <Input
          id={`${fieldIdPrefix}slug`}
          value={slugValue}
          onChange={(event) => patch({ slug: event.target.value })}
          placeholder="e.g. tenant1"
          disabled={showShareToggles && slugMode === "same"}
        />
        {showShareToggles && slugMode === "same" && (
          <div className="text-xs text-muted-foreground">Using the primary company slug.</div>
        )}
        <FieldError message={errors.slug} />
      </div>

      <div className="space-y-2 md:col-span-2">
        <FieldLabel>Website</FieldLabel>
        <Input
          value={value.website}
          onChange={(event) => patch({ website: event.target.value })}
          placeholder="https://company.com"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>Bank Account No.</FieldLabel>
        <Input
          value={value.bankAccountNo}
          onChange={(event) => patch({ bankAccountNo: event.target.value })}
          placeholder="Account number"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>IFSC Code</FieldLabel>
        <Input
          value={value.ifscCode}
          onChange={(event) =>
            patch({ ifscCode: event.target.value.toUpperCase().replace(/\s+/g, "") })
          }
          placeholder="e.g. HDFC0001234"
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>Status</FieldLabel>
        <select
          className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          value={value.status}
          onChange={(event) => patch({ status: event.target.value as CompanyDetails["status"] })}
        >
          <option value="Active">Active</option>
          <option value="Trial">Trial</option>
          <option value="Suspended">Suspended</option>
        </select>
      </div>
    </>
  )
}
